
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "../Layout/Layout";
import {
  ArrowLeft,
  Info,
  Trophy,
  Search,
  UserCheck,
  CheckCircle,
  AlertCircle,
  Clock,
  Download,
  FileText
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ClipLoader } from "react-spinners";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import certificateBackground from '../assets/Img/Certificate/airaa-membership-certificate.webp';
import { MemberService } from "../Services/MemberService";
import { CertificateApprovalService } from "../Services/CertificateApprovalService";

// Helper to check if membership is active (endDate >= today)
const isMembershipActive = (memberships) => {
  if (!memberships || memberships.length === 0) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return memberships.some((m) => {
    const endDate = new Date(m.membershipEndDate);
    return endDate >= today;
  });
};


// Helper to get active membership object
const getActiveMembership = (memberships) => {
  if (!memberships || memberships.length === 0) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return memberships.find((m) => {
    const endDate = new Date(m.membershipEndDate);
    return endDate >= today;
  });
};

// Format date for certificate (e.g., "1st April 2026")
const formatCertificateDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString("en-IN", { month: "long" });
  const year = date.getFullYear();
  let suffix = "th";
  if (day === 1 || day === 21 || day === 31) suffix = "st";
  else if (day === 2 || day === 22) suffix = "nd";
  else if (day === 3 || day === 23) suffix = "rd";
  return `${day}${suffix} ${month} ${year}`;
};

// ========== CERTIFICATE HELPERS ==========
const fitText = (ctx, text, maxWidth, initialFontSize = 48, isBold = false, fontFamily = "Arial") => {
  let fontSize = initialFontSize;
  do {
    ctx.font = `${isBold ? "bold " : ""}${fontSize}px ${fontFamily}`;
    fontSize--;
  } while (ctx.measureText(text).width > maxWidth && fontSize > 10);
  return fontSize;
};

const wrapText = (ctx, text, maxWidth) => {
  if (!text || text.trim() === "") return [];
  const words = text.split(" ");
  const lines = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const testLine = currentLine + " " + word;
    if (ctx.measureText(testLine).width < maxWidth) {
      currentLine = testLine;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
};

const DownloadDuplicateCertifcate = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const raidFromQuery = searchParams.get("raid");
  const encGuidFromQuery = searchParams.get("guid");
  const [raidInput, setRaidInput] = useState("");
  const [guidInput, setGuidInput] = useState("");
  const [memberData, setMemberData] = useState(null);
  const [membershipActive, setMembershipActive] = useState(false);
  const [activeMembership, setActiveMembership] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [activating, setActivating] = useState(false);
  const [downloadingCert, setDownloadingCert] = useState(false);
  const [allowtoDownloadCertificate, setAllowtoDownloadCertificate] = useState(false);

  // Auto-fetch if raid is present in query
  useEffect(() => {
    if (raidFromQuery) {
      const raId = raidFromQuery ? atob(decodeURIComponent(raidFromQuery)) : null;
      const guid = encGuidFromQuery ? atob(decodeURIComponent(encGuidFromQuery)) : null;
      console.log("Extracted from query - RA ID:", raId, "GUID:", guid);
      setRaidInput(raId || "");
      setGuidInput(guid || "");
      handleSearch(raId);
    }
  }, [raidFromQuery, encGuidFromQuery]);

  const handleSearch = async (raidToSearch = raidInput) => {
    if (!raidToSearch.trim()) {
      toast.warn("Please enter an RA ID.");
      return;
    }

    setIsSearching(true);
    setMemberData(null);
    setMembershipActive(false);
    setActiveMembership(null);

    try {
      const response = await MemberService.GetBoUserDetailsByRaid(raidToSearch);
      if (response?.isSuccess && response.user) {
        const user = response.user;
        const mappedData = {
          boUserID: user.boUserID,
          raId: user.raId,
          boUserRegistrationID: user.boUserRegistrationID,
          companyName: user.companyName,
          companyAddress: user.companyAddress,
          companyWebsite: user.companyWebsite,
          establishmentYear: user.establishmentYear,
          registeredWithCentralGovt: user.registeredWithCentralGovt,
          contactPersonName: user.contactPersonName,
          mobile: user.contactPersonMobile,
          email: user.contactPersonEmail,
          pan: user.pan,
        };
        setMemberData(mappedData);

        const active = isMembershipActive(response.memberships);
        setMembershipActive(active);
        if (active) {
          const activeMem = getActiveMembership(response.memberships);
          setActiveMembership(activeMem);
        }

        if (active) {
          toast.success("Active membership found!");
        } else {
          toast.info("Member verified. You can activate free membership.");
        }
      } else {
        toast.error(response?.message || "Member not found.");
        setMemberData(null);
      }
    } catch (err) {
      toast.error("Failed to load member details. Please try again.");
      setMemberData(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handleFreeActivation = async () => {
    if (!memberData || !memberData.boUserID) {
      toast.error("Member data not found. Please search again.");
      return;
    }

    setActivating(true);
    try {
      // Build request data from memberData (map fields as per API expectation)
      const reqData = {
        raId: memberData.raId || "",
        boUserRegistrationID: memberData.boUserRegistrationID || "",
        companyName: memberData.companyName || "",
        website: memberData.companyWebsite || "",
        yearsEstablished: memberData.establishmentYear ? String(memberData.establishmentYear) : "",
        address: memberData.companyAddress || "",
        companyStatus: "", // default if not available
        governmentRegistered: "",
        directorName: memberData.contactPersonName || "",
        email: memberData.email || "",
        mobile: memberData.mobile || "",
        phone: "", // using mobile as phone
        panNumber: memberData.pan || "",
        BOUserID: memberData.boUserID,
      };

      const response = await MemberService.ActivateFreeMembership(reqData);
      if (response.isSuccess) {
        toast.success(response.message || "Membership activated successfully!");
        // Refresh member details to get the new membership
        await handleSearch(memberData.raId);
      } else {
        toast.error(response.message || "Activation failed. Please contact support.");
      }
    } catch (error) {
      console.error("Activation error:", error);
      toast.error("Unable to activate free membership. Please contact support.");
    } finally {
      setActivating(false);
    }
  };

  // ====================== CERTIFICATE DOWNLOAD ======================
  const downloadCertificate = async () => {
    const certificateTrackPayload = {
      Guid: guidInput
    }
    const trackCertificateDownload = await CertificateApprovalService.CertificateReDownloadUpdate(certificateTrackPayload);
    if (trackCertificateDownload?.isSuccess) {
      toast.success(trackCertificateDownload.message || "Certificate downloaded successfully!");
    }
    else {
      toast.error(trackCertificateDownload.message || "Failed to download certificate.");
      setAllowtoDownloadCertificate(true);
      return;
    }

    if (!memberData || !activeMembership) {
      toast.error("Membership data not available.");
      return;
    }
    console.log(activeMembership)
    setDownloadingCert(true);
    toast.info("Generating certificate, please wait...");

    try {
      const certData = {
        companyName: memberData.companyName?.trim() || "N/A",
        companyAddress: memberData.companyAddress?.trim() || "",
        membershipNumber: activeMembership.membershipNumber || memberData.boUserRegistrationID || "N/A",
        startDate: formatCertificateDate(activeMembership.membershipStartDate),
        endDate: formatCertificateDate(activeMembership.membershipEndDate),
      };

      const img = await new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = "Anonymous";
        image.onload = () => resolve(image);
        image.onerror = (err) => {
          console.error("Image load failed:", err);
          reject(new Error("Failed to load certificate background image."));
        };
        image.src = certificateBackground;
      });

      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d", { alpha: false });
      ctx.drawImage(img, 0, 0, img.width, img.height);
      const centerX = img.width / 2;
      const maxTextWidth = img.width * 0.75;

      // Company Name
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#000000";
      const nameFontSize = fitText(ctx, certData.companyName, maxTextWidth, 30, true);
      ctx.font = `bold ${nameFontSize}px Arial`;
      ctx.fillText(certData.companyName, centerX, img.height * 0.41);

      // Company Address
      ctx.font = "20px Arial";
      const addressLines = wrapText(ctx, certData.companyAddress, maxTextWidth * 0.90);
      let yPosition = img.height * 0.43;
      const lineHeight = 24;
      addressLines.forEach((line) => {
        ctx.fillText(line, centerX, yPosition);
        yPosition += lineHeight;
      });

      // Membership Period
      ctx.fillStyle = "#000000";
      const periodText = `${certData.startDate} to ${certData.endDate}`;
      const periodFontSize = fitText(ctx, periodText, maxTextWidth, 30, true);
      ctx.font = `bold ${periodFontSize}px Arial`;
      ctx.fillText(periodText, centerX, img.height * 0.56);

      // Membership Number
      ctx.fillStyle = "#000000";
      const membershipText = `Membership No: ${certData.membershipNumber}`;
      const memFontSize = fitText(ctx, membershipText, maxTextWidth, 30, true);
      ctx.font = `bold ${memFontSize}px Arial`;
      ctx.fillText(membershipText, centerX, img.height * 0.65);

      const imgData = canvas.toDataURL("image/png", 0.95);
      const pdf = new jsPDF({
        orientation: img.width > img.height ? "landscape" : "portrait",
        unit: "px",
        format: [img.width, img.height],
        compress: true,
      });
      pdf.addImage(imgData, "PNG", 0, 0, img.width, img.height);
      pdf.save(`AIRAA_Certificate_${certData.companyName.replace(/[^a-zA-Z0-9]/g, "_")}.pdf`);

    } catch (error) {
      console.error("Certificate generation error:", error);
      toast.error("Failed to generate certificate. Please try again.");
    } finally {
      setDownloadingCert(false);
    }
  };

  // Loading overlay
  if (isSearching || activating || downloadingCert) {
    return (
      <Layout>
        <div className="fixed inset-0 backdrop-blur-sm bg-white/60 flex flex-col items-center justify-center z-50 gap-4">
          <ClipLoader color="#10b981" size={56} />
          <p className="text-emerald-700 font-semibold text-sm tracking-wide animate-pulse">
            {activating
              ? "Activating your free membership..."
              : downloadingCert
                ? "Generating your certificate..."
                : "Loading..."}
          </p>
        </div>
        <ToastContainer position="bottom-left" autoClose={3500} />
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet />
      <div className="md:container max-w-5xl mx-auto bg-white shadow-xl rounded-2xl p-8 mt-10 mb-16">
        {/* Back button */}
        <button
          onClick={() => navigate("/registration")}
          className="flex items-center gap-1 text-gray-600 hover:text-emerald-600 mb-6 transition-colors"
        >
          <ArrowLeft size={18} /> Back to Registration
        </button>

        <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-2">
          Download Membership <span className="text-emerald-600">Certificate</span>
        </h2>
        <p className="text-gray-600 text-center text-sm mb-8">
          Enter your RA ID to download a duplicate copy of your membership certificate. If you haven't activated your membership yet, you can do so for free and get instant access to your certificate.
        </p>

        {/* Search Section */}
        <div className="max-w-xl mx-auto mb-8">
          <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl border border-emerald-100 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Search size={18} className="text-emerald-600" />
              <span className="font-semibold text-gray-700">Find Your Membership</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                placeholder="Enter RA ID"
                value={raidInput}
                onChange={(e) => setRaidInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="flex-1 px-4 py-3 border border-gray-200 rounded-xl bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none transition"
                disabled={isSearching}
              />
              <button
                onClick={() => handleSearch()}
                disabled={isSearching || !raidInput.trim()}
                className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 text-white font-medium rounded-xl flex items-center justify-center gap-2 transition-colors shadow-sm"
              >
                {isSearching ? (
                  <ClipLoader size={18} color="#fff" />
                ) : (
                  <>
                    <Search size={18} /> Find
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Enter the RA ID you used during registration.
            </p>
          </div>
        </div>

        {/* Member Details & Activation */}
        {memberData ? (
          <>
            {/* Member Summary Card */}
            <div className="bg-white rounded-2xl border border-emerald-100 shadow-md p-6 mb-8 animate-fade-in">
              <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
                <UserCheck size={20} className="text-emerald-500" />
                <span className="font-bold text-gray-800">Member Verified</span>
                {membershipActive ? (
                  <span className="ml-auto text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full flex items-center gap-1">
                    <CheckCircle size={12} /> Active Member
                  </span>
                ) : (
                  <span className="ml-auto text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full flex items-center gap-1">
                    <AlertCircle size={12} /> Free Activation Available
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm">
                <div>
                  <span className="text-gray-600">Company</span>
                  <p className="font-semibold text-gray-800">{memberData.companyName}</p>
                </div>
                <div>
                  <span className="text-gray-600">RA ID</span>
                  <p className="font-semibold text-gray-800 font-mono">{memberData.raId}</p>
                </div>
                <div>
                  <span className="text-gray-600">Membership ID</span>
                  <p className="font-semibold text-gray-800 font-mono">{memberData.boUserRegistrationID}</p>
                </div>
                <div>
                  <span className="text-gray-600">Contact Person</span>
                  <p className="font-semibold text-gray-800">{memberData.contactPersonName}</p>
                </div>
                <div>
                  <span className="text-gray-600">Email</span>
                  <p className="font-semibold text-gray-800">{memberData.email}</p>
                </div>
                <div>
                  <span className="text-gray-600">Mobile</span>
                  <p className="font-semibold text-gray-800">{memberData.mobile}</p>
                </div>
                <div>
                  <span className="text-gray-600">PAN</span>
                  <p className="font-semibold text-gray-800">{memberData.pan || "—"}</p>
                </div>
              </div>
            </div>

            {membershipActive ? (
              // Active membership view
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <CheckCircle size={40} className="text-green-500 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-green-800 mb-1">
                  Your Membership is Active!
                </h3>
                <p className="text-green-700 text-sm max-w-md mx-auto">
                  Valid until{" "}
                  {activeMembership
                    ? formatCertificateDate(activeMembership.membershipEndDate)
                    : "N/A"}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-4 mt-4">
                  <button
                    onClick={() => navigate("/")}
                    className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition flex items-center gap-2"
                  >
                    Go to Home
                  </button>
                  <button
                    onClick={downloadCertificate}
                    disabled={downloadingCert}
                    className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition flex items-center gap-2 shadow-md"
                  >
                    <Download size={16} />
                    {!downloadingCert ? "Download Certificate" : "Generating..."}
                  </button>
                </div>
                {allowtoDownloadCertificate && (
                  <div className="mt-4 flex items-start gap-2 text-xs text-red-600 bg-red-100 p-3 rounded-lg">
                    <AlertCircle size={14} className="shrink-0 mt-0.5" />To request duplicate certificate <button onClick={() => navigate(`/membership?raid=${memberData.raId}`)} className="underline font-medium">Click here</button>
                  </div>
                )}
              </div>
            ) : (
              // Free activation section
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
                <Trophy size={40} className="text-blue-500 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-blue-800 mb-1">
                  Activate Your Free Membership
                </h3>
                <p className="text-blue-700 text-sm max-w-md mx-auto mb-4">
                  Your membership is currently inactive. Click the button below to activate it for free.
                </p>
                <button
                  onClick={handleFreeActivation}
                  disabled={activating}
                  className="px-8 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all shadow-md mx-auto"
                >
                  {activating ? (
                    <ClipLoader size={20} color="#fff" />
                  ) : (
                    <>
                      <CheckCircle size={18} /> Activate Free Membership
                    </>
                  )}
                </button>
                <div className="mt-4 flex items-start gap-2 text-xs text-blue-600 bg-blue-100 p-3 rounded-lg">
                  <Info size={14} className="shrink-0 mt-0.5" />
                  <span>
                    No payment required. Your membership will be valid for one year from activation.
                    You can download your membership certificate immediately after activation.
                  </span>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12 text-gray-400">
            <Trophy size={48} className="mx-auto mb-4 opacity-30" />
            <p>Enter your RA ID above to view membership details and activate for free.</p>
          </div>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        * { font-family: 'Plus Jakarta Sans', sans-serif; }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fade-in 0.4s ease-out forwards;
        }
      `}</style>

      <ToastContainer position="bottom-left" autoClose={3500} />
    </Layout>
  );
};

export default DownloadDuplicateCertifcate;